import React, { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon, FilterIcon, CheckCircleIcon, FileTextIcon, ChevronDownIcon, FolderIcon, DownloadIcon, TrashIcon, ArrowLeftIcon, FolderPlusIcon, ChevronRightIcon } from 'lucide-react';
import { DocumentUploader } from '../../components/applications/DocumentUploader';
import { useToast } from '../../context/ToastContext';
// Folder type definition
interface Folder {
  id: string;
  name: string;
  type: 'main' | 'subfolder';
  parentId?: string;
  path?: string;
}
// Document type definition
interface Document {
  id: string;
  fileName: string;
  type: string;
  application: string;
  borrower: string;
  uploadDate: string;
  fileSize: number;
  verified: boolean;
  folderId: string;
}
export function ApplicationDocuments() {
  const {
    addToast
  } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [applicationFilter, setApplicationFilter] = useState('all');
  const [showUploader, setShowUploader] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Folder[]>([]);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderType, setNewFolderType] = useState<'main' | 'subfolder'>('main');
  // Mock data for folders
  const [folders, setFolders] = useState<Folder[]>([{
    id: 'folder-1',
    name: 'Strauss, Sarah & Bray, Michael – Purchase – Sept 30, 2025',
    type: 'main'
  }, {
    id: 'folder-2',
    name: 'Chen, David – Refinance – Aug 15, 2025',
    type: 'main'
  }, {
    id: 'folder-3',
    name: 'Johnson, Sarah – Purchase – Oct 10, 2025',
    type: 'main'
  }, {
    id: 'subfolder-1',
    name: 'For File',
    type: 'subfolder',
    parentId: 'folder-1'
  }, {
    id: 'subfolder-2',
    name: 'Docs Not Needed',
    type: 'subfolder',
    parentId: 'folder-1'
  }, {
    id: 'subfolder-3',
    name: 'Sent to Lender',
    type: 'subfolder',
    parentId: 'folder-1'
  }, {
    id: 'subfolder-4',
    name: 'Signing Package',
    type: 'subfolder',
    parentId: 'folder-1'
  }, {
    id: 'subfolder-5',
    name: 'For File',
    type: 'subfolder',
    parentId: 'folder-2'
  }, {
    id: 'subfolder-6',
    name: 'Docs Not Needed',
    type: 'subfolder',
    parentId: 'folder-2'
  }, {
    id: 'subfolder-7',
    name: 'Sent to Lender',
    type: 'subfolder',
    parentId: 'folder-2'
  }, {
    id: 'subfolder-8',
    name: 'Signing Package',
    type: 'subfolder',
    parentId: 'folder-2'
  }, {
    id: 'subfolder-9',
    name: 'For File',
    type: 'subfolder',
    parentId: 'folder-3'
  }, {
    id: 'subfolder-10',
    name: 'Docs Not Needed',
    type: 'subfolder',
    parentId: 'folder-3'
  }, {
    id: 'subfolder-11',
    name: 'Sent to Lender',
    type: 'subfolder',
    parentId: 'folder-3'
  }, {
    id: 'subfolder-12',
    name: 'Signing Package',
    type: 'subfolder',
    parentId: 'folder-3'
  }]);
  // Mock data for documents
  const [documents, setDocuments] = useState<Document[]>([{
    id: '1',
    fileName: 'Meridian Savings L Scheer 5678 Oct24-Jan25.pdf',
    type: 'Bank Statement',
    application: 'APP-2025-0001',
    borrower: 'Sarah Strauss',
    uploadDate: '2025-01-31',
    fileSize: 255.56,
    verified: true,
    folderId: 'subfolder-1'
  }, {
    id: '2',
    fileName: 'Pay Stub Acme Corporation Michael Bray Jan 1-14 2025.pdf',
    type: 'Pay Stub',
    application: 'APP-2025-0001',
    borrower: 'Michael Bray',
    uploadDate: '2025-01-30',
    fileSize: 2.28,
    verified: true,
    folderId: 'subfolder-1'
  }, {
    id: '3',
    fileName: 'Meridian Checking Sarah Strauss 1234 Oct24-Jan25.pdf',
    type: 'Bank Statement',
    application: 'APP-2025-0001',
    borrower: 'Sarah Strauss',
    uploadDate: '2025-01-29',
    fileSize: 328.37,
    verified: true,
    folderId: 'subfolder-3'
  }, {
    id: '4',
    fileName: 'Johnson Family Tax Return 2024.pdf',
    type: 'Tax Return',
    application: 'APP-2025-0002',
    borrower: 'Sarah Johnson',
    uploadDate: '2025-01-28',
    fileSize: 1024.12,
    verified: false,
    folderId: 'subfolder-9'
  }, {
    id: '5',
    fileName: 'Chen Family W2 2024.pdf',
    type: 'W2',
    application: 'APP-2025-0003',
    borrower: 'David Chen',
    uploadDate: '2025-01-25',
    fileSize: 512.75,
    verified: false,
    folderId: 'subfolder-5'
  }, {
    id: '6',
    fileName: 'Purchase Agreement - Strauss & Bray.pdf',
    type: 'Purchase Agreement',
    application: 'APP-2025-0001',
    borrower: 'Sarah Strauss & Michael Bray',
    uploadDate: '2025-01-20',
    fileSize: 1250.45,
    verified: true,
    folderId: 'subfolder-4'
  }, {
    id: '7',
    fileName: 'Loan Estimate - Chen Refinance.pdf',
    type: 'Loan Estimate',
    application: 'APP-2025-0003',
    borrower: 'David Chen',
    uploadDate: '2025-01-18',
    fileSize: 875.3,
    verified: true,
    folderId: 'subfolder-7'
  }]);
  // Update breadcrumbs when current folder changes
  useEffect(() => {
    if (!currentFolder) {
      setBreadcrumbs([]);
      return;
    }
    const currentFolderObj = folders.find(f => f.id === currentFolder);
    if (!currentFolderObj) return;
    if (currentFolderObj.type === 'main') {
      setBreadcrumbs([currentFolderObj]);
    } else {
      const parentFolder = folders.find(f => f.id === currentFolderObj.parentId);
      if (parentFolder) {
        setBreadcrumbs([parentFolder, currentFolderObj]);
      }
    }
  }, [currentFolder, folders]);
  // Get folders for the current view
  const getCurrentFolders = () => {
    if (!currentFolder) {
      return folders.filter(folder => folder.type === 'main');
    } else {
      const currentFolderObj = folders.find(f => f.id === currentFolder);
      if (currentFolderObj?.type === 'main') {
        return folders.filter(folder => folder.parentId === currentFolder);
      }
      return [];
    }
  };
  // Get documents for the current folder
  const getCurrentDocuments = () => {
    if (!currentFolder) return [];
    return documents.filter(doc => doc.folderId === currentFolder);
  };
  // Navigate to folder
  const navigateToFolder = (folderId: string) => {
    setCurrentFolder(folderId);
  };
  // Navigate back
  const navigateBack = () => {
    if (!currentFolder) return;
    const currentFolderObj = folders.find(f => f.id === currentFolder);
    if (!currentFolderObj) return;
    if (currentFolderObj.type === 'subfolder' && currentFolderObj.parentId) {
      setCurrentFolder(currentFolderObj.parentId);
    } else {
      setCurrentFolder(null);
    }
  };
  // Handle document upload
  const handleDocumentUpload = (files: File[]) => {
    if (!currentFolder) {
      addToast('Please select a folder before uploading documents', 'error');
      return;
    }
    // In a real app, this would upload the files to a server
    const newDocuments = files.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      fileName: file.name,
      type: 'Unknown',
      application: 'APP-2025-0001',
      borrower: 'Unknown',
      uploadDate: new Date().toISOString().split('T')[0],
      fileSize: +(file.size / 1024).toFixed(2),
      verified: false,
      folderId: currentFolder
    }));
    setDocuments([...documents, ...newDocuments]);
    addToast(`${files.length} document(s) uploaded successfully`, 'success');
    setShowUploader(false);
  };
  // Handle delete document
  const handleDeleteDocument = (documentId: string) => {
    // Show a confirmation dialog
    if (window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      setDocuments(documents.filter(doc => doc.id !== documentId));
      addToast('Document deleted successfully', 'success');
    }
  };
  // Handle create folder
  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      addToast('Folder name cannot be empty', 'error');
      return;
    }
    const newFolder: Folder = {
      id: `folder-${Date.now()}`,
      name: newFolderName,
      type: newFolderType,
      parentId: newFolderType === 'subfolder' ? currentFolder : undefined
    };
    setFolders([...folders, newFolder]);
    setShowCreateFolderModal(false);
    setNewFolderName('');
    addToast('Folder created successfully', 'success');
  };
  // Format file size
  const formatFileSize = (sizeInKB: number) => {
    if (sizeInKB < 1000) {
      return `${sizeInKB.toFixed(2)} KB`;
    } else {
      return `${(sizeInKB / 1024).toFixed(2)} MB`;
    }
  };
  // Get unique document types for filter
  const documentTypes = ['all', ...new Set(documents.map(doc => doc.type))];
  // Get unique applications for filter
  const applications = ['all', ...new Set(documents.map(doc => doc.application))];
  // Filter documents based on search term and filters
  const filteredDocuments = getCurrentDocuments().filter(doc => {
    const matchesSearch = doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) || doc.borrower.toLowerCase().includes(searchTerm.toLowerCase()) || doc.application.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || doc.type === typeFilter;
    const matchesApplication = applicationFilter === 'all' || doc.application === applicationFilter;
    return matchesSearch && matchesType && matchesApplication;
  });
  return <div className="space-y-4" data-id="element-957">
      <div className="flex items-center justify-between" data-id="element-958">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-959">
          Document Repository
        </h1>
        <div className="flex space-x-2" data-id="element-960">
          <button onClick={() => setShowCreateFolderModal(true)} className="btn btn-outline" disabled={currentFolder && folders.find(f => f.id === currentFolder)?.type === 'subfolder'} data-id="element-961">
            <FolderPlusIcon className="mr-2 h-4 w-4" data-id="element-962" />
            Create Folder
          </button>
          <button onClick={() => setShowUploader(!showUploader)} className="btn btn-primary" disabled={!currentFolder} data-id="element-963">
            <PlusIcon className="mr-2 h-4 w-4" data-id="element-964" />
            Upload Documents
          </button>
        </div>
      </div>

      {/* Breadcrumb navigation */}
      <div className="flex items-center space-x-2 text-sm" data-id="element-965">
        <button onClick={() => setCurrentFolder(null)} className="text-primary-600 hover:text-primary-800 font-medium" data-id="element-966">
          Home
        </button>
        {breadcrumbs.map((folder, index) => <Fragment key={folder.id} data-id="element-967">
            <ChevronRightIcon className="h-4 w-4 text-neutral-400" data-id="element-968" />
            <button onClick={() => navigateToFolder(folder.id)} className={`${index === breadcrumbs.length - 1 ? 'text-neutral-600 font-medium' : 'text-primary-600 hover:text-primary-800 font-medium'}`} data-id="element-969">
              {folder.name}
            </button>
          </Fragment>)}
      </div>

      {/* Back button when in a folder */}
      {currentFolder && <button onClick={navigateBack} className="inline-flex items-center px-3 py-1.5 border border-neutral-300 text-sm font-medium rounded text-neutral-700 hover:bg-neutral-50 transition-colors" data-id="element-970">
          <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-971" />
          Back
        </button>}

      {showUploader && <div className="card p-4 mb-4" data-id="element-972">
          <h2 className="text-lg font-medium text-neutral-900 mb-3" data-id="element-973">
            Upload Documents
          </h2>
          <DocumentUploader onUpload={handleDocumentUpload} data-id="element-974" />
          <div className="mt-4 flex justify-end" data-id="element-975">
            <button onClick={() => setShowUploader(false)} className="btn btn-outline mr-2" data-id="element-976">
              Cancel
            </button>
          </div>
        </div>}

      {/* Create Folder Modal */}
      {showCreateFolderModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-977">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-978">
            <h2 className="text-lg font-medium text-neutral-900 mb-4" data-id="element-979">
              Create New Folder
            </h2>
            <div className="mb-4" data-id="element-980">
              <label className="block text-sm font-medium text-neutral-700 mb-1" data-id="element-981">
                Folder Type
              </label>
              <select value={newFolderType} onChange={e => setNewFolderType(e.target.value as 'main' | 'subfolder')} className="w-full p-2 border border-neutral-300 rounded-md" disabled={!currentFolder} data-id="element-982">
                <option value="main" data-id="element-983">Main Folder</option>
                <option value="subfolder" disabled={!currentFolder} data-id="element-984">
                  Subfolder
                </option>
              </select>
              {newFolderType === 'main' && <p className="text-xs text-neutral-500 mt-1" data-id="element-985">
                  Format: LastName, FirstName – Purpose – Closing Date
                </p>}
            </div>
            <div className="mb-4" data-id="element-986">
              <label className="block text-sm font-medium text-neutral-700 mb-1" data-id="element-987">
                Folder Name
              </label>
              <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder={newFolderType === 'main' ? 'Smith, John – Purchase – Oct 15, 2025' : 'Folder Name'} className="w-full p-2 border border-neutral-300 rounded-md" data-id="element-988" />
            </div>
            <div className="flex justify-end space-x-2" data-id="element-989">
              <button onClick={() => setShowCreateFolderModal(false)} className="btn btn-outline" data-id="element-990">
                Cancel
              </button>
              <button onClick={handleCreateFolder} className="btn btn-primary" data-id="element-991">
                Create Folder
              </button>
            </div>
          </div>
        </div>}

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row justify-between space-y-3 md:space-y-0 md:space-x-4" data-id="element-992">
        <div className="relative max-w-md w-full" data-id="element-993">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-994">
            <SearchIcon className="h-5 w-5 text-neutral-400" data-id="element-995" />
          </div>
          <input type="text" className="input pl-10 w-full" placeholder="Search by filename, borrower, or application ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-996" />
        </div>
        <div className="flex space-x-3" data-id="element-997">
          <div className="relative" data-id="element-998">
            <select className="select pr-10 appearance-none" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-id="element-999">
              {documentTypes.map(type => <option key={type} value={type} data-id="element-1000">
                  {type === 'all' ? 'All Document Types' : type}
                </option>)}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral-500" data-id="element-1001">
              <ChevronDownIcon className="h-4 w-4" data-id="element-1002" />
            </div>
          </div>
          <div className="relative" data-id="element-1003">
            <select className="select pr-10 appearance-none" value={applicationFilter} onChange={e => setApplicationFilter(e.target.value)} data-id="element-1004">
              {applications.map(app => <option key={app} value={app} data-id="element-1005">
                  {app === 'all' ? 'All Applications' : app}
                </option>)}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral-500" data-id="element-1006">
              <ChevronDownIcon className="h-4 w-4" data-id="element-1007" />
            </div>
          </div>
          <button className="btn btn-outline" data-id="element-1008">
            <FilterIcon className="mr-2 h-4 w-4" data-id="element-1009" />
            More Filters
          </button>
        </div>
      </div>

      {/* Folders Grid */}
      {(!currentFolder || currentFolder && folders.find(f => f.id === currentFolder)?.type === 'main') && <div className="mb-6" data-id="element-1010">
          <h2 className="text-lg font-medium text-neutral-900 mb-3" data-id="element-1011">
            {currentFolder ? 'Subfolders' : 'Client Folders'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1012">
            {getCurrentFolders().map(folder => <div key={folder.id} onClick={() => navigateToFolder(folder.id)} className="bg-white rounded-lg border border-neutral-200 p-4 flex items-center cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-1013">
                <div className="mr-4 text-neutral-400" data-id="element-1014">
                  <FolderIcon className="h-10 w-10" data-id="element-1015" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1016">
                  <h3 className="text-sm font-medium text-neutral-900 truncate" data-id="element-1017">
                    {folder.name}
                  </h3>
                  <p className="text-xs text-neutral-500" data-id="element-1018">
                    {documents.filter(doc => doc.folderId === folder.id).length}{' '}
                    documents
                  </p>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-neutral-400" data-id="element-1019" />
              </div>)}
          </div>
        </div>}

      {/* Documents Table */}
      {currentFolder && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1020">
          <div className="p-4 border-b border-neutral-200" data-id="element-1021">
            <h2 className="text-lg font-medium text-neutral-900" data-id="element-1022">
              Documents in {folders.find(f => f.id === currentFolder)?.name}
            </h2>
          </div>
          {filteredDocuments.length > 0 ? <div className="table-container" data-id="element-1023">
              <table className="table" data-id="element-1024">
                <thead className="table-header" data-id="element-1025">
                  <tr data-id="element-1026">
                    <th className="table-header-cell" data-id="element-1027">Document Name</th>
                    <th className="table-header-cell" data-id="element-1028">Type</th>
                    <th className="table-header-cell" data-id="element-1029">Application</th>
                    <th className="table-header-cell" data-id="element-1030">Borrower</th>
                    <th className="table-header-cell" data-id="element-1031">Upload Date</th>
                    <th className="table-header-cell" data-id="element-1032">Size</th>
                    <th className="table-header-cell" data-id="element-1033">Status</th>
                    <th className="table-header-cell" data-id="element-1034">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body" data-id="element-1035">
                  {filteredDocuments.map(doc => <tr key={doc.id} className="table-row" data-id="element-1036">
                      <td className="table-cell" data-id="element-1037">
                        <div className="flex items-center" data-id="element-1038">
                          <FileTextIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-1039" />
                          <Link to={`/applications/documents/${doc.id}`} className="text-primary-600 hover:text-primary-800 font-medium" data-id="element-1040">
                            {doc.fileName}
                          </Link>
                        </div>
                      </td>
                      <td className="table-cell" data-id="element-1041">{doc.type}</td>
                      <td className="table-cell" data-id="element-1042">
                        <Link to={`/applications/${doc.application}`} className="text-primary-600 hover:text-primary-800 font-medium" data-id="element-1043">
                          {doc.application}
                        </Link>
                      </td>
                      <td className="table-cell" data-id="element-1044">{doc.borrower}</td>
                      <td className="table-cell" data-id="element-1045">
                        {new Date(doc.uploadDate).toLocaleDateString()}
                      </td>
                      <td className="table-cell" data-id="element-1046">
                        {formatFileSize(doc.fileSize)}
                      </td>
                      <td className="table-cell" data-id="element-1047">
                        {doc.verified ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800" data-id="element-1048">
                            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-1049" />
                            Verified
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800" data-id="element-1050">
                            Pending
                          </span>}
                      </td>
                      <td className="table-cell" data-id="element-1051">
                        <div className="flex space-x-2" data-id="element-1052">
                          <Link to={`/applications/documents/${doc.id}`} className="text-neutral-400 hover:text-neutral-600" title="View" data-id="element-1053">
                            <FileTextIcon className="h-5 w-5" data-id="element-1054" />
                          </Link>
                          <button className="text-neutral-400 hover:text-neutral-600" title="Download" data-id="element-1055">
                            <DownloadIcon className="h-5 w-5" data-id="element-1056" />
                          </button>
                          <button onClick={() => handleDeleteDocument(doc.id)} className="text-neutral-400 hover:text-danger-600" title="Delete" data-id="element-1057">
                            <TrashIcon className="h-5 w-5" data-id="element-1058" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="p-8 text-center text-neutral-500" data-id="element-1059">
              <p data-id="element-1060">No documents found in this folder.</p>
              <p className="mt-2 text-sm" data-id="element-1061">
                {searchTerm ? 'Try adjusting your search criteria.' : 'Upload documents to get started.'}
              </p>
            </div>}
        </div>}
    </div>;
}